import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, get, errMsg } from "@/lib/api";

const T = [["strongly_recommend", "Strongly recommend ≥"], ["recommend", "Recommend ≥"], ["consider", "Consider ≥"]];

export function ThresholdsCard({ job, lb, canEdit }) {
  const qc = useQueryClient();
  const [th, setTh] = useState(lb.thresholds);
  const [custom, setCustom] = useState(lb.custom_thresholds);
  useEffect(() => { setTh(lb.thresholds); setCustom(lb.custom_thresholds); }, [lb.thresholds, lb.custom_thresholds]);
  const payload = (c, t) => ({ thresholds: c ? Object.fromEntries(T.map(([k]) => [k, Number(t[k])])) : null });
  const refresh = () => setTimeout(() => qc.invalidateQueries({ queryKey: ["leaderboard", job.id] }), 1500);
  const dirty = custom !== lb.custom_thresholds || T.some(([k]) => Number(th[k]) !== Number(lb.thresholds[k]));
  const save = async () => {
    const prev = payload(lb.custom_thresholds, lb.thresholds);
    try {
      await api.put(`/jobs/${job.id}/fit-thresholds`, payload(custom, th)); refresh();
      toast.success("Thresholds applied. Recommendations update in the background.", { duration: 5000, action: { label: "Undo", onClick: async () => {
        try { await api.put(`/jobs/${job.id}/fit-thresholds`, prev); setTh(lb.thresholds); setCustom(lb.custom_thresholds); refresh(); toast.success("Previous thresholds restored"); } catch (e) { toast.error(errMsg(e)); }
      } } });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const bucket = (t) => { const c = { strongly_recommend: 0, recommend: 0, consider: 0 }; lb.rows.forEach((r) => { const s = r.fit_score; if (s == null) return; if (s >= t.strongly_recommend) c.strongly_recommend++; else if (s >= t.recommend) c.recommend++; else if (s >= t.consider) c.consider++; }); return c; };
  const was = bucket(lb.thresholds);
  const will = bucket(Object.fromEntries(T.map(([k]) => [k, Number(th[k])])));
  return (
    <section className="panel space-y-3 p-5" data-testid="thresholds-card">
      <h3 className="text-base font-semibold">Recommendation thresholds</h3>
      <label className="flex items-center gap-2 text-sm text-ink-secondary"><Checkbox checked={custom} disabled={!canEdit} onCheckedChange={(v) => setCustom(!!v)} data-testid="thresholds-custom-toggle" /> Use custom thresholds for this job (otherwise the organisation default)</label>
      {T.map(([k, l]) => (
        <div key={k} className="flex items-center justify-between text-sm"><span className="text-ink-secondary">{l}</span>
          <input type="range" step={0.1} min={0} max={10} value={th[k]} disabled={!custom || !canEdit} onChange={(e) => setTh({ ...th, [k]: e.target.value })} className="mx-3 flex-1 accent-[#1F4D3A]" aria-label={l} data-testid={`threshold-slider-${k}`} />
          <input type="number" step={0.1} min={0} max={10} value={th[k]} disabled={!custom || !canEdit} onChange={(e) => setTh({ ...th, [k]: e.target.value })} className="h-8 w-20 rounded-lg border border-line px-2 text-right tabular-nums" data-testid={`threshold-${k}`} /></div>
      ))}
      <p className="text-xs text-ink-muted">Below {th.consider} is “Not recommended”. Nobody is ever rejected automatically.</p>
      {canEdit && dirty && (
        <div className="space-y-1 rounded-[6px] border border-brand/15 bg-brand-tint/40 p-3 text-sm" data-testid="thresholds-preview" aria-live="polite">
          <p className="text-xs font-semibold text-brand">Recommendations would become…</p>
          {T.map(([k, l]) => <p key={k} className="flex justify-between"><span className="text-ink-secondary">{l.replace(" ≥", "")}</span><span className="tabular-nums">{was[k]} → <strong>{will[k]}</strong></span></p>)}
        </div>
      )}
      {canEdit && <div className="flex items-center gap-2"><Button size="sm" variant="outline" disabled={!dirty} onClick={save} data-testid="thresholds-save-btn">Apply</Button>{dirty && <span className="text-xs text-ink-muted">Nothing is saved until you click Apply.</span>}</div>}
    </section>
  );
}

export function ManagersCard({ job }) {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["job-managers", job.id], queryFn: () => get(`/jobs/${job.id}/hiring-managers`) });
  const toggle = async (id) => {
    const ids = data.filter((m) => (m.id === id ? !m.assigned : m.assigned)).map((m) => m.id);
    try { await api.put(`/jobs/${job.id}/hiring-managers`, { ids }); qc.invalidateQueries({ queryKey: ["job-managers", job.id] }); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="panel space-y-2 p-5" data-testid="managers-card">
      <h3 className="text-base font-semibold">Hiring managers who can decide</h3>
      {data.length ? data.map((m) => <label key={m.id} className="flex items-center gap-2 text-sm"><Checkbox checked={m.assigned} onCheckedChange={() => toggle(m.id)} data-testid={`manager-toggle-${m.id}`} />{m.name} <span className="text-xs text-ink-muted">{m.email}</span></label>)
        : <p className="text-sm text-ink-muted">No hiring manager accounts yet.</p>}
    </section>
  );
}
